import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import QUERY_TAGS from "../search/queryTags";
import { runQuery } from "../search/queryCache";

import { buildSearchParams, pagelessKey, stableKey } from "./accessoryFilters";
import { mapSearchPage } from "./accessoryMappers";
import { searchAccessories } from "./accessoriesService";

const ACCESSORY_STALE_TIME = 600000;

const EMPTY_FEED = {
  key: null,
  items: [],
  meta: null,
  status: "idle",
  error: null,
};

const useAccessoryFeed = ({
  containerOptionId,
  categoryCode,
  term,
  filters,
  coordinates,
  enabled = true,
}) => {
  const params = useMemo(
    () =>
      buildSearchParams({
        containerOptionId,
        categoryCode,
        term,
        filters,
        coordinates,
      }),
    [containerOptionId, categoryCode, term, filters, coordinates]
  );

  const resetKey = useMemo(() => pagelessKey(params), [params]);

  const [page, setPage] = useState(0);
  const [pageKey, setPageKey] = useState(resetKey);
  const [feed, setFeed] = useState(EMPTY_FEED);

  const sequenceRef = useRef(0);

  if (pageKey !== resetKey) {
    setPageKey(resetKey);
    setPage(0);
  }

  const active = Boolean(enabled && containerOptionId);
  const current = feed.key === resetKey ? feed : EMPTY_FEED;

  const load = useCallback(
    (targetPage, { force = false } = {}) => {
      const request = { ...params, page: targetPage };
      const sequence = sequenceRef.current + 1;

      sequenceRef.current = sequence;

      setFeed((previous) => ({
        ...(previous.key === resetKey ? previous : EMPTY_FEED),
        key: resetKey,
        status: targetPage === 0 ? "loading" : "loading-more",
        error: null,
      }));

      return runQuery({
        key: `accessories:search:${stableKey(request)}`,
        tag: QUERY_TAGS.PRODUCT_ACCESSORIES,
        staleTime: ACCESSORY_STALE_TIME,
        force,
        fetcher: () => searchAccessories(request),
      })
        .then((data) => {
          if (sequenceRef.current !== sequence) return;

          const mapped = mapSearchPage(data, coordinates);

          if (mapped.page !== targetPage) return;

          setFeed((previous) => {
            const base = previous.key === resetKey ? previous : EMPTY_FEED;

            return {
              key: resetKey,
              items:
                targetPage === 0 ? mapped.items : [...base.items, ...mapped.items],
              meta: mapped,
              status: "success",
              error: null,
            };
          });
        })
        .catch((error) => {
          if (sequenceRef.current !== sequence) return;

          setFeed((previous) => ({
            ...(previous.key === resetKey ? previous : EMPTY_FEED),
            key: resetKey,
            status: "error",
            error,
          }));
        });
    },
    [params, resetKey, coordinates]
  );

  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!active || pageKey !== resetKey) return;

    loadRef.current(page);
  }, [active, page, pageKey, resetKey]);

  const meta = current.meta;
  const hasMore = Boolean(meta && !meta.isLast);
  const isFetching = current.status === "loading" || current.status === "loading-more";

  const loadMore = useCallback(() => {
    if (!hasMore || isFetching) return;

    setPage((value) => value + 1);
  }, [hasMore, isFetching]);

  const refetch = useCallback(() => {
    setPage(0);
    loadRef.current(0, { force: true });
  }, []);

  return {
    items: current.items,
    total: meta?.total ?? 0,
    page,
    hasMore,
    loadMore,
    refetch,
    isFirstLoad: current.status === "loading" && !current.items.length,
    isRequerying: current.status === "loading" && current.items.length > 0,
    isLoadingMore: current.status === "loading-more",
    isFetching,
    isSuccess: current.status === "success",
    isError: current.status === "error",
    isEmpty: current.status === "success" && !current.items.length,
    error: current.error,
    params,
  };
};

export default useAccessoryFeed;
